import { getUploadedScrewData } from "store/selectors/ScrewSelectors";
import { AppThunk } from "..";
import { api } from "../../services/Api";
import {
  updatePredictionData,
  updatePredictionRunningState,
  updatePredictionSubmitState,
  updateScrewPredictionData,
} from "../reducers/PredictionReducers";
import { getUploadedLocatorData } from "../selectors/LocatorSelectors";
import { getCustomerUploadData } from "../selectors/CustomerUploadSelectors";

export const onRunPredictionAction = (): AppThunk => (dispatch, getState) => {
  const locatorData = getUploadedLocatorData(getState());
  const screwData = getUploadedScrewData(getState());
  const customerUpload = getCustomerUploadData(getState());

  if (!locatorData && !screwData && !customerUpload) return;

  dispatch(updatePredictionRunningState("loading"));

  const locatorid = locatorData?.locatorid;
  const sampleid = screwData?.[0]?.sampleid; // Use first screw for prediction
  const clientuploadid = customerUpload?.clientuploadid;

  if (clientuploadid) {
    let attempts = 0;
    const maxAttempts = 30; // 1 minute max (2s * 30)
    
    const pollPredictions = () => {
      attempts++;
      api
        .get(`/client-uploads/${clientuploadid}/predictions`)
        .then((response) => {
          if (response.status === "PREDICTED" && response.predictions?.length > 0) {
            if (response.modeltype === "screw") {
              dispatch(updateScrewPredictionData(response.predictions));
              dispatch(updatePredictionData(null));
            } else {
              dispatch(updatePredictionData(response.predictions));
              dispatch(updateScrewPredictionData(null));
            }
            dispatch(updatePredictionRunningState("success"));
          } else if (response.status === "PREDICTION_FAILED") {
            dispatch(updatePredictionRunningState("error"));
          } else if (attempts < maxAttempts) {
            setTimeout(pollPredictions, 2000);
          } else {
            dispatch(updatePredictionRunningState("error"));
          }
        })
        .catch(() => {
          dispatch(updatePredictionRunningState("error"));
        });
    };

    // Start polling
    pollPredictions();
  } else if (locatorid) {
    dispatch(updatePredictionRunningState("loading"));
    api
      .post("/get-locator-prediction", `locatorid=${locatorid}`)
      .then((response) => {
        dispatch(updatePredictionData(response));
        dispatch(updatePredictionRunningState("success"));
      })
      .catch(() => {
        dispatch(updatePredictionRunningState("error"));
      });
  } else if (sampleid) {
    dispatch(updatePredictionRunningState("loading"));
    api
      .post("/get-screw-prediction", `sample_id=${sampleid}`)
      .then((response) => {
        dispatch(updatePredictionData(response?.items || []));
        dispatch(updatePredictionRunningState("success"));
      })
      .catch(() => {
        dispatch(updatePredictionRunningState("error"));
      });
  }
};

export const onAddLocatorToDatasetAction =
  (payload: { locatorid: string; typeid: string; lengthid: string; imagetype: string }): AppThunk =>
  (dispatch) => {
    dispatch(updatePredictionSubmitState("loading"));

    const { locatorid, typeid, lengthid, imagetype } = payload;
    const formDataBody = [
      `locatorid=${encodeURIComponent(locatorid)}`,
      `typeid=${encodeURIComponent(typeid)}`,
      `lengthid=${encodeURIComponent(lengthid)}`,
      `imagetype=${encodeURIComponent(imagetype)}`
    ].join("&");

    api
      .post("/add-locator-to-dataset", formDataBody)
      .then(() => dispatch(updatePredictionSubmitState("success")))
      .catch(() => dispatch(updatePredictionSubmitState("error")));
  };

export const onAddScrewsToDatasetAction =
  (payload: any): AppThunk =>
  (dispatch) => {
    dispatch(updatePredictionSubmitState("loading"));

    const { sample_id } = payload;
    const refProperty = payload.propertyvalues.items.find(
      (item: any) => item.propertyname.toLowerCase() === "ref"
    );

    if (!refProperty?.categoricalvalue) {
      console.error("Failed to find ref property or its value in payload:", {
        payload,
        availableProperties: payload.propertyvalues.items.map((item: any) => ({
          name: item.propertyname,
          value: item.categoricalvalue
        }))
      });
      dispatch(updatePredictionSubmitState("error"));
      return;
    }

    const formDataBody = [
      `sample_ids=${encodeURIComponent(sample_id)}`, // Keep as single for prediction flow
      `categoricalvalue=${encodeURIComponent(refProperty.categoricalvalue)}`
    ].join("&");

    api
      .post("/add-screw-to-dataset", formDataBody)
      .then((response) => {
        // Handle the new list response - the API now returns a list of SampleWithPropertyValues
        if (response && response.length > 0) {
          dispatch(updatePredictionSubmitState("success"));
        } else {
          dispatch(updatePredictionSubmitState("error"));
        }
      })
      .catch(() => {
        dispatch(updatePredictionSubmitState("error"));
      });
  };
